import log from 'loglevel';
import * as React from 'react';
import { connect } from 'react-redux';
import { Set } from 'immutable';
import { setGreetings as setGreetingsAction } from '../../store/actions';
import { behavior as behaviorSelectors, config as configSelectors } from '../../store/selectors';
import { LocationInfo, UserInfo, Authtoken } from '../../types';
import { TasksBackendClient, TaskDefinition, TaskType } from '../../backends/tasks';
//import { GreetingStyle } from '../../backends/tasks/greetings';

type ReduxState = any;

export type InvalidateToken = () => Promise<void>;

export interface TasksManagerProps {
    invalidateToken?: InvalidateToken;
    fetchTasksRetryCount?: number;
}

interface StateProps {
    tasksBackendClient: TasksBackendClient;
    delayMs: number;
    locationInfo?: LocationInfo;
    userInfoExists?: boolean;
    userInfo?: UserInfo;
    authtoken?: Authtoken;
}

interface DispatchProps {
    setGreetings: (greetingTasks: Array<TaskDefinition>) => void;
}

type AllProps = TasksManagerProps & StateProps & DispatchProps;

const TasksManager: React.FC<AllProps> = ({
    invalidateToken,
    fetchTasksRetryCount,
    delayMs,
    tasksBackendClient,
    locationInfo,
    userInfoExists,
    userInfo,
    authtoken,
    setGreetings,
}: AllProps) => {
    const processTasks = React.useCallback(
        (tasks: Array<TaskDefinition>) => {
            setGreetings(tasks.filter(({ type }) => type === TaskType.POPUP));
            // for testing:
            //setGreetings([{
            //  type: TaskType.POPUP,
            //  id: 'foobar123',
            //  message: 'this is just a [test](http://google.de) greeting',
            //  style: GreetingStyle.DEFAULT
            //}]);
        },
        [setGreetings]
    );

    // NOTE: storing current values in refs, as we need to access those from within a setTimeout(),
    //   which would otherwise always see the "old" values from when the setTimeout() was triggered:
    const locationInfoRef = React.useRef<LocationInfo | undefined>(undefined);
    const userInfoRef = React.useRef<UserInfo | undefined>(undefined);
    const authtokenRef = React.useRef<Authtoken | undefined>(undefined);
    React.useEffect(() => {
        locationInfoRef.current = locationInfo;
        userInfoRef.current = userInfo;
        authtokenRef.current = authtoken;
    }, [locationInfo, userInfo, authtoken]);

    const retriesEnabled = React.useRef<boolean>(false);
    const retriesCount = React.useRef<number>(0);
    const enableRetries = () => {
        retriesEnabled.current = true;
    };
    const resetRetries = () => {
        retriesEnabled.current = false;
        retriesCount.current = 0;
    };
    const isRetryAppropriate = () => {
        log.debug(
            'retries appropriate',
            retriesEnabled.current,
            retriesCount.current,
            fetchTasksRetryCount || 3
        );
        return retriesEnabled.current && retriesCount.current < (fetchTasksRetryCount || 3);
    };
    const addRetry = () => {
        retriesCount.current += 1;
    };

    const fetchActiveTasks = React.useCallback(async () => {
        const handleUnauthorized = () => {
            log.warn('backend responded with 401 unauthorized, will retry after authtoken change');
            enableRetries();

            if (invalidateToken) {
                log.info('signalling invalidateToken');
                invalidateToken();
            }
        };

        return tasksBackendClient
            ?.fetchActiveTasks(locationInfoRef.current, userInfoRef.current, authtokenRef.current)
            .then((tasks) => {
                processTasks(tasks);
                resetRetries();
            })
            .catch((error) => {
                log.warn('error while fetching active tasks', error);
                //processTasks([]);  // for testing

                if (error?.status == 401) {
                    handleUnauthorized();
                }
            });
    }, [invalidateToken, tasksBackendClient]);

    const prevTimeoutRef = React.useRef<any>(undefined);
    const maybeCancelPrevTimeout = () => {
        if (prevTimeoutRef.current) {
            clearTimeout(prevTimeoutRef.current);
            prevTimeoutRef.current = undefined;
        }
    };

    const scheduleFetchTasks = React.useCallback(() => {
        if (delayMs !== undefined) {
            maybeCancelPrevTimeout();
            prevTimeoutRef.current = setTimeout(fetchActiveTasks, delayMs);
        }
    }, [delayMs]);

    const prevAuthtokenRef = React.useRef<Authtoken | undefined>(undefined);
    React.useEffect(() => {
        // NOTE: fetch tasks only when
        //   a) we don't have any userInfo  or
        //   b)
        //     1. if all userInfo is present
        //     2. but don't trigger on subsequently changing authtoken
        if (
            !userInfoExists ||
            (userInfo?.customerId &&
                userInfo?.userId &&
                authtoken &&
                (prevAuthtokenRef.current === undefined || prevAuthtokenRef.current === authtoken))
        ) {
            scheduleFetchTasks();
            prevAuthtokenRef.current = authtoken;
        }
    }, [
        delayMs,
        tasksBackendClient,
        userInfoExists,
        userInfo?.customerId,
        userInfo?.userId,
        authtoken,
    ]);

    React.useEffect(() => {
        if (authtoken) {
            if (isRetryAppropriate()) {
                addRetry();
                fetchActiveTasks();
            }
        }
    }, [authtoken]);

    /**
   * for testing/debugging greetings:
   *
  React.useEffect(() => {
    setGreetings([{
      id: 'foobar',
      type: TaskType.POPUP,
      message: 'this is a dummy task',
      style: GreetingStyle.LOUD,
    }]);
  }, []);
  */

    return null;
};

const mapStateToProps = (state: ReduxState, ownProps: TasksManagerProps): StateProps => {
    return {
        tasksBackendClient: configSelectors.tasksBackendClient(state.config),
        delayMs: behaviorSelectors.greetingsDelayMs(state.behavior),
        locationInfo: configSelectors.browserLocation(state.config),
        userInfoExists: configSelectors.userInfoExists(state.config),
        userInfo: {
            customerId: configSelectors.customerId(state.config),
            userId: configSelectors.userId(state.config),
        },
        authtoken: configSelectors.authtoken(state.config),
    };
};

const mapDispatchToProps = (
    dispatch: React.Dispatch<any>,
    ownProps: TasksManagerProps
): DispatchProps => {
    return {
        setGreetings: (greetingTasks: Array<TaskDefinition>) =>
            dispatch(setGreetingsAction(greetingTasks)),
    };
};

export default connect<StateProps, DispatchProps, TasksManagerProps>(
    mapStateToProps,
    mapDispatchToProps
)(TasksManager);
